import { AxiosResponse } from 'axios';
import mapKeys from 'lodash-es/mapKeys';
import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { IBasePageContent, IPageContent } from 'src/interfaces/PageContent';
import { createDedupedFetcher } from 'src/stores/lib/dedupePromise';

interface PageContentsState {
  pageContents: IPageContent[];
  pageContentsById?: { [key: string]: IPageContent };
  pageContentsByName?: { [key: string]: IPageContent };

  isLoading: boolean;

  deleteAllPageContents: () => Promise<void | AxiosResponse<null>>;
  deletePageContent: (id: string) => Promise<void | AxiosResponse<null>>;
  fetchPageContents: () => Promise<void | AxiosResponse<null>>;
  resetPageContents: () => void;
  savePageContent: (
    pageContent: IBasePageContent,
    id?: string,
  ) => Promise<void | AxiosResponse<IPageContent[]>>;
}

const usePageContentsStore = create<PageContentsState>(set => {
  const getPageContents = async () => {
    return SECURE_API.get<IPageContent[]>('pageContents/')
      .then(({ data }) => {
        set({
          pageContents: data,
          pageContentsById: mapKeys(data, 'id'),
          pageContentsByName: mapKeys(data, 'name'),
          isLoading: false
        });
      })
      .catch((error: string) => {
        set({
          pageContents: [],
          pageContentsById: undefined,
          pageContentsByName: undefined,
          isLoading: false
        });
        console.log({ error });
      });
  };

  const fetchPageContentsDeduped = createDedupedFetcher(async () => {
    set({ isLoading: true });
    return getPageContents();
  });

  return {
    pageContents: [],
    pageContentsById: undefined,
    pageContentsByName: undefined,

    isLoading: false,

    fetchPageContents: fetchPageContentsDeduped,

    savePageContent: async (pageContent: IBasePageContent, id?: string) => {
      set({ isLoading: true });
      const savePageContentPromise = id
        ? SECURE_API.put<IPageContent[]>(
            `pageContents/update/${id}`,
            pageContent,
          )
        : SECURE_API.post<IPageContent[]>(
            'pageContents/save',
            pageContent,
          );
      return savePageContentPromise
        .catch((error: unknown) => {
          console.log({ error });
          throw error;
        })
        .finally(() => {
          getPageContents();
        });
    },
    deletePageContent: async id => {
      set({ isLoading: true });
      return SECURE_API.delete<null>(`pageContents/delete/${id}`)
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getPageContents();
        });
    },
    deleteAllPageContents: async () => {
      set({ isLoading: true });
      return SECURE_API.delete<null>(`pageContents/delete/bulk`)
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getPageContents();
        });
    },
    resetPageContents: () => set({ pageContents: [] }),
  };
});

export default usePageContentsStore;
